/**
 * Import/Export utilities for Slaaploods Verlofbeheer
 * Supports: JSON full export, CSV per-category export/import
 */

const BOM = '\uFEFF';
const SEP = ';';

function downloadCSV(filename, header, rows) {
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadJSON(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const sep = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
  const rows = lines.slice(1).map(line => {
    const vals = line.split(sep).map(v => v.trim().replace(/^['"]|['"]$/g, ''));
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  }).filter(row => Object.values(row).some(v => v));
  return { headers, rows };
}

// Convert dd-mm-yyyy or dd/mm/yyyy to yyyy-mm-dd
function normalizeDatum(val) {
  if (!val) return '';
  val = val.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const match = val.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (match) return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  return val;
}

function fmtDatumNL(d) {
  if (!d) return '';
  const [y, m, dd] = d.split('-');
  return `${dd}-${m}-${y}`;
}

// ============================
// CSV EXPORT PER CATEGORY
// ============================

export function exportVerlofCSV(verlofaanvragen, verlofTypes) {
  const header = ['Medewerker', 'Type', 'Status', 'Startdatum', 'Einddatum', 'Starttijd', 'Eindtijd', 'Uren', 'Overuren gebruikt', 'Open ziek', 'Opmerkingen', 'Jaar'].join(SEP);
  const rows = verlofaanvragen
    .sort((a, b) => (a.MedewerkerNaam || '').localeCompare(b.MedewerkerNaam || '') || (a.StartDatum || '').localeCompare(b.StartDatum || ''))
    .map(v => [
      v.MedewerkerNaam || '',
      v.Type || '',
      v.Status || '',
      fmtDatumNL(v.StartDatum),
      fmtDatumNL(v.EindDatum),
      v.StartTijd || '',
      v.EindTijd || '',
      v.Uren || 0,
      v.overurenGebruikt || 0,
      v.isOpenZiek ? 'ja' : '',
      (v.Reden || '').replace(/;/g, ','),
      v.jaar || '',
    ].join(SEP));
  downloadCSV(`slaaploods-verlof-export-${new Date().toISOString().split('T')[0]}.csv`, header, rows);
}

export function exportOverurenCSV(overuren) {
  const header = ['Medewerker', 'Datum', 'Uren', 'Omschrijving', 'Status'].join(SEP);
  const rows = overuren
    .sort((a, b) => (a.medewerkerNaam || a.MedewerkerNaam || '').localeCompare(b.medewerkerNaam || b.MedewerkerNaam || ''))
    .map(o => [
      o.medewerkerNaam || o.MedewerkerNaam || '',
      fmtDatumNL(o.Datum || o.datum),
      o.Uren || o.uren || 0,
      (o.Reden || o.reden || '').replace(/;/g, ','),
      o.Status || o.status || '',
    ].join(SEP));
  downloadCSV(`slaaploods-overuren-export-${new Date().toISOString().split('T')[0]}.csv`, header, rows);
}

export function exportFlexPlanningCSV(flexPlanning) {
  const header = ['Flexkracht', 'Datum', 'Starttijd', 'Eindtijd', 'Uren', 'Omschrijving', 'Status'].join(SEP);
  const rows = flexPlanning
    .sort((a, b) => (a.flexkrachtNaam || '').localeCompare(b.flexkrachtNaam || '') || (a.datum || '').localeCompare(b.datum || ''))
    .map(p => [
      p.flexkrachtNaam || '',
      fmtDatumNL(p.datum),
      p.startTijd || '',
      p.eindTijd || '',
      p.uren || 0,
      (p.omschrijving || '').replace(/;/g, ','),
      p.planStatus || 'ingepland',
    ].join(SEP));
  downloadCSV(`slaaploods-flex-planning-export-${new Date().toISOString().split('T')[0]}.csv`, header, rows);
}

// ============================
// CSV IMPORT PER CATEGORY
// ============================

export function parseVerlofCSV(text, medewerkers, verlofTypes, importUser) {
  const { headers, rows } = parseCSV(text);
  const records = [];
  const warnings = [];
  const errors = [];

  const colMap = {
    medewerker: headers.findIndex(h => ['medewerker', 'naam', 'name', 'employee'].includes(h)),
    type: headers.findIndex(h => ['type', 'type verlof', 'verloftype'].includes(h)),
    status: headers.findIndex(h => ['status'].includes(h)),
    startdatum: headers.findIndex(h => ['startdatum', 'start', 'van', 'from', 'begindatum'].includes(h)),
    einddatum: headers.findIndex(h => ['einddatum', 'eind', 'tot', 'to', 'end', 'tm', 't/m'].includes(h)),
    starttijd: headers.findIndex(h => ['starttijd', 'begintijd', 'van tijd'].includes(h)),
    eindtijd: headers.findIndex(h => ['eindtijd', 'tot tijd'].includes(h)),
    uren: headers.findIndex(h => ['uren', 'hours', 'aantal uren'].includes(h)),
    opmerkingen: headers.findIndex(h => ['opmerkingen', 'reden', 'opmerking', 'reason', 'toelichting'].includes(h)),
    overuren: headers.findIndex(h => ['overuren gebruikt', 'overuren'].includes(h)),
    openziek: headers.findIndex(h => ['open ziek', 'openziek'].includes(h)),
    jaar: headers.findIndex(h => ['jaar', 'year'].includes(h)),
  };

  const getCol = (row, key) => {
    const idx = colMap[key];
    if (idx < 0) return '';
    return Object.values(row)[idx] || '';
  };

  if (colMap.medewerker < 0) { errors.push('Kolom "Medewerker" niet gevonden'); return { records, warnings, errors }; }
  if (colMap.startdatum < 0) { errors.push('Kolom "Startdatum" niet gevonden'); return { records, warnings, errors }; }

  const typeAliasMap = {};
  (verlofTypes || []).forEach(t => {
    typeAliasMap[t.key] = t.key;
    typeAliasMap[t.text.toLowerCase()] = t.key;
    typeAliasMap[t.label.toLowerCase()] = t.key;
  });

  rows.forEach((row, i) => {
    const lineNr = i + 2;
    const naam = getCol(row, 'medewerker');
    if (!naam) return;

    const mw = medewerkers.find(m =>
      (m.Naam || m.naam || '').toLowerCase() === naam.toLowerCase() ||
      (m.Naam || m.naam || '').toLowerCase().startsWith(naam.toLowerCase())
    );
    if (!mw) {
      errors.push(`Regel ${lineNr}: Medewerker "${naam}" niet gevonden`);
      return;
    }

    const rawType = getCol(row, 'type').toLowerCase() || 'vakantie';
    const type = typeAliasMap[rawType] || rawType;
    if (!typeAliasMap[rawType]) {
      warnings.push(`Regel ${lineNr}: Type "${rawType}" onbekend, wordt als "${type}" opgeslagen`);
    }

    const startDatum = normalizeDatum(getCol(row, 'startdatum'));
    const eindDatum = normalizeDatum(getCol(row, 'einddatum')) || startDatum;
    if (!startDatum || !/^\d{4}-\d{2}-\d{2}$/.test(startDatum)) {
      errors.push(`Regel ${lineNr}: Ongeldige startdatum "${getCol(row, 'startdatum')}"`);
      return;
    }

    const rawStatus = getCol(row, 'status').toLowerCase() || 'goedgekeurd';
    const statusMap = {
      goedgekeurd: 'goedgekeurd', approved: 'goedgekeurd', goed: 'goedgekeurd',
      ingediend: 'ingediend', pending: 'ingediend',
      afgekeurd: 'afgekeurd', rejected: 'afgekeurd', afgewezen: 'afgekeurd',
      ingetrokken: 'ingetrokken',
    };
    const status = statusMap[rawStatus] || 'goedgekeurd';

    const uren = parseFloat(getCol(row, 'uren')) || 0;
    const jaar = parseInt(getCol(row, 'jaar')) || new Date(startDatum).getFullYear();
    const openZiek = getCol(row, 'openziek').toLowerCase();

    records.push({
      medewerkerId: mw.Id || mw.id,
      medewerkerNaam: mw.Naam || mw.naam,
      ingediendDoor: importUser?.id || '',
      ingediendDoorNaam: importUser?.Naam || 'Import',
      type,
      status,
      startDatum,
      eindDatum,
      beginTijd: getCol(row, 'starttijd') || null,
      eindTijd: getCol(row, 'eindtijd') || null,
      uren,
      reden: getCol(row, 'opmerkingen'),
      jaar,
      isOpenZiek: openZiek === 'ja' || openZiek === 'true' || openZiek === '1',
      gebruikOveruren: false,
      overurenGebruikt: parseFloat(getCol(row, 'overuren')) || 0,
    });
  });

  return { records, warnings, errors };
}

export function parseOverurenCSV(text, medewerkers) {
  const { headers, rows } = parseCSV(text);
  const records = [];
  const warnings = [];
  const errors = [];

  const colMap = {
    medewerker: headers.findIndex(h => ['medewerker', 'naam', 'name'].includes(h)),
    datum: headers.findIndex(h => ['datum', 'date'].includes(h)),
    uren: headers.findIndex(h => ['uren', 'hours', 'aantal'].includes(h)),
    omschrijving: headers.findIndex(h => ['omschrijving', 'reden', 'opmerking', 'opmerkingen', 'reason'].includes(h)),
    status: headers.findIndex(h => ['status'].includes(h)),
  };

  const getCol = (row, key) => {
    const idx = colMap[key];
    if (idx < 0) return '';
    return Object.values(row)[idx] || '';
  };

  if (colMap.medewerker < 0) { errors.push('Kolom "Medewerker" niet gevonden'); return { records, warnings, errors }; }
  if (colMap.datum < 0) { errors.push('Kolom "Datum" niet gevonden'); return { records, warnings, errors }; }
  if (colMap.uren < 0) { errors.push('Kolom "Uren" niet gevonden'); return { records, warnings, errors }; }

  rows.forEach((row, i) => {
    const lineNr = i + 2;
    const naam = getCol(row, 'medewerker');
    if (!naam) return;

    const mw = medewerkers.find(m =>
      (m.Naam || m.naam || '').toLowerCase() === naam.toLowerCase() ||
      (m.Naam || m.naam || '').toLowerCase().startsWith(naam.toLowerCase())
    );
    if (!mw) {
      errors.push(`Regel ${lineNr}: Medewerker "${naam}" niet gevonden`);
      return;
    }

    const datum = normalizeDatum(getCol(row, 'datum'));
    if (!datum || !/^\d{4}-\d{2}-\d{2}$/.test(datum)) {
      errors.push(`Regel ${lineNr}: Ongeldige datum "${getCol(row, 'datum')}"`);
      return;
    }

    const uren = parseFloat(getCol(row, 'uren'));
    if (isNaN(uren) || uren <= 0) {
      errors.push(`Regel ${lineNr}: Ongeldige uren "${getCol(row, 'uren')}"`);
      return;
    }

    const rawStatus = getCol(row, 'status').toLowerCase() || 'goedgekeurd';
    const status = rawStatus === 'ingediend' || rawStatus === 'pending' ? 'ingediend' : 'goedgekeurd';

    records.push({
      medewerkerId: mw.Id || mw.id,
      medewerkerNaam: mw.Naam || mw.naam,
      datum,
      uren,
      reden: getCol(row, 'omschrijving'),
      status,
    });
  });

  return { records, warnings, errors };
}

export function parseFlexPlanningCSV(text, medewerkers) {
  const { headers, rows } = parseCSV(text);
  const records = [];
  const warnings = [];
  const errors = [];

  const colMap = {
    flexkracht: headers.findIndex(h => ['flexkracht', 'medewerker', 'naam', 'name'].includes(h)),
    datum: headers.findIndex(h => ['datum', 'date'].includes(h)),
    starttijd: headers.findIndex(h => ['starttijd', 'begintijd', 'van'].includes(h)),
    eindtijd: headers.findIndex(h => ['eindtijd', 'tot'].includes(h)),
    uren: headers.findIndex(h => ['uren', 'hours', 'aantal'].includes(h)),
    omschrijving: headers.findIndex(h => ['omschrijving', 'opmerking', 'opmerkingen', 'reden', 'toelichting', 'locatie'].includes(h)),
  };

  const getCol = (row, key) => {
    const idx = colMap[key];
    if (idx < 0) return '';
    return Object.values(row)[idx] || '';
  };

  if (colMap.flexkracht < 0) { errors.push('Kolom "Flexkracht" niet gevonden'); return { records, warnings, errors }; }
  if (colMap.datum < 0) { errors.push('Kolom "Datum" niet gevonden'); return { records, warnings, errors }; }

  rows.forEach((row, i) => {
    const lineNr = i + 2;
    const naam = getCol(row, 'flexkracht');
    if (!naam) return;

    const mw = medewerkers.find(m =>
      (m.Naam || m.naam || '').toLowerCase() === naam.toLowerCase() ||
      (m.Naam || m.naam || '').toLowerCase().startsWith(naam.toLowerCase())
    );
    if (!mw) {
      errors.push(`Regel ${lineNr}: Flexkracht "${naam}" niet gevonden`);
      return;
    }

    const datum = normalizeDatum(getCol(row, 'datum'));
    if (!datum || !/^\d{4}-\d{2}-\d{2}$/.test(datum)) {
      errors.push(`Regel ${lineNr}: Ongeldige datum "${getCol(row, 'datum')}"`);
      return;
    }

    records.push({
      flexkrachtId: mw.Id || mw.id,
      flexkrachtNaam: mw.Naam || mw.naam,
      datum,
      startTijd: getCol(row, 'starttijd') || '09:00',
      eindTijd: getCol(row, 'eindtijd') || '17:00',
      uren: parseFloat(getCol(row, 'uren')) || 8,
      omschrijving: getCol(row, 'omschrijving'),
      planStatus: 'ingepland',
    });
  });

  return { records, warnings, errors };
}

export function downloadTemplate(type) {
  const templates = {
    verlof: {
      filename: 'template-verlof-import.csv',
      header: 'Medewerker;Type;Status;Startdatum;Einddatum;Starttijd;Eindtijd;Uren;Opmerkingen',
      example: 'Jan de Vries;vakantie;goedgekeurd;10-03-2025;14-03-2025;;;40;Voorjaarsvakantie',
    },
    overuren: {
      filename: 'template-overuren-import.csv',
      header: 'Medewerker;Datum;Uren;Omschrijving;Status',
      example: 'Jan de Vries;15-03-2025;2;Extra gewerkt;goedgekeurd',
    },
    flex: {
      filename: 'template-flex-planning-import.csv',
      header: 'Flexkracht;Datum;Starttijd;Eindtijd;Uren;Omschrijving',
      example: 'Piet Jansen;20-03-2025;09:00;17:00;8;Vervanging',
    },
  };
  const t = templates[type];
  if (!t) return;
  downloadCSV(t.filename, t.header, [t.example]);
}

// ============================
// EXISTING FULL EXPORT (JSON)
// ============================

export function exportData({ medewerkers, verlofaanvragen, overuren, feestdagen, vakantiePerioden, verlofTypes, roosterWijzigingen, flexPlanning, flexUren, flexUitbetalingen, jaararchief }) {
  const data = {
    versie: '2.0',
    exportDatum: new Date().toISOString(),
    applicatie: 'Slaaploods Verlofbeheer',
    medewerkers: medewerkers.map(m => ({
      id: m.Id || m.id, naam: m.Naam || m.naam, email: m.Email || m.email,
      afdeling: m.Afdeling || m.afdeling, rol: m.Rol || m.rol,
      isManager: m.IsManager || m.isManager || false, isAdmin: m.IsAdmin || m.isAdmin || false,
      status: m.Status || m.status || 'actief',
      contractUren: m.ContractUren || m.contractUren || 0,
      indienstDatum: m.IndienstDatum || m.indienstDatum,
      meegenomenUren: m.MeegenomenUren || m.meegenomenUren || 0,
      werkrooster: m.Werkrooster || m.werkrooster || null,
      flexEnabled: m.FlexEnabled || m.flexEnabled || false,
      telefoon: m.Telefoon || m.telefoon || '',
    })),
    verlofaanvragen: verlofaanvragen.map(v => ({
      id: v.Id || v.id, medewerkerId: v.medewerkerId,
      medewerkerNaam: v.MedewerkerNaam || v.medewerkerNaam,
      type: v.Type || v.type, status: v.Status || v.status,
      startDatum: v.StartDatum || v.startDatum, eindDatum: v.EindDatum || v.eindDatum,
      startTijd: v.StartTijd || v.startTijd || null, eindTijd: v.EindTijd || v.eindTijd || null,
      uren: v.Uren || v.uren || 0, reden: v.Reden || v.reden || '',
      isOpenZiek: v.isOpenZiek || false, overurenGebruikt: v.overurenGebruikt || 0,
      ingediendDoor: v.ingediendDoor || '', jaar: v.jaar,
    })),
    overuren: overuren.map(o => ({
      id: o.Id || o.id, medewerkerId: o.medewerkerId,
      medewerkerNaam: o.medewerkerNaam || o.MedewerkerNaam,
      datum: o.Datum || o.datum, uren: o.Uren || o.uren || 0,
      reden: o.Reden || o.reden || '', status: o.Status || o.status,
    })),
    feestdagen: feestdagen.map(f => ({ id: f.Id || f.id, datum: f.Datum || f.datum, naam: f.Naam || f.naam })),
    vakantiePerioden: (vakantiePerioden || []).map(vp => ({
      id: vp.Id || vp.id, naam: vp.naam, startDatum: vp.startDatum, eindDatum: vp.eindDatum,
    })),
    verlofTypes: verlofTypes.map(t => ({ ...t })),
    roosterWijzigingen: (roosterWijzigingen || []).map(rw => ({
      id: rw.Id || rw.id, medewerkerId: rw.medewerkerId, medewerkerNaam: rw.medewerkerNaam,
      datum: rw.datum, heleDag: rw.heleDag, startTijd: rw.startTijd, eindTijd: rw.eindTijd,
      omschrijving: rw.omschrijving, vervangDatum: rw.vervangDatum || null,
    })),
    flexPlanning: (flexPlanning || []).map(p => ({
      id: p.Id || p.id, flexkrachtId: p.flexkrachtId, flexkrachtNaam: p.flexkrachtNaam,
      datum: p.datum, startTijd: p.startTijd, eindTijd: p.eindTijd,
      uren: p.uren || 0, omschrijving: p.omschrijving || '',
      planStatus: p.planStatus || 'ingepland',
    })),
    flexUren: (flexUren || []).map(u => ({
      id: u.Id || u.id, flexkrachtId: u.flexkrachtId, flexkrachtNaam: u.flexkrachtNaam,
      datum: u.datum, startTijd: u.startTijd, eindTijd: u.eindTijd,
      uren: u.uren || 0, omschrijving: u.omschrijving || '',
    })),
    flexUitbetalingen: (flexUitbetalingen || []).map(u => ({
      id: u.Id || u.id, flexkrachtId: u.flexkrachtId, flexkrachtNaam: u.flexkrachtNaam,
      datum: u.datum, uren: u.uren || 0, omschrijving: u.omschrijving || '',
    })),
    jaararchief: jaararchief || [],
  };
  downloadJSON(`slaaploods-export-${new Date().toISOString().split('T')[0]}.json`, data);
}

export function leesImportBestand(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.versie || !data.applicatie) {
          reject(new Error('Ongeldig bestandsformaat — geen Slaaploods export.'));
          return;
        }
        resolve(data);
      } catch (err) {
        reject(new Error('Kan bestand niet lezen — ongeldig JSON formaat.'));
      }
    };
    reader.onerror = () => reject(new Error('Fout bij het lezen van het bestand.'));
    reader.readAsText(file);
  });
}

export function exportMedewerkersCSV(medewerkers, verlofaanvragen, overuren, verlofTypes, jaar) {
  const header = 'Naam;Afdeling;Contract (u/w);Verlofuren;Meegenomen;Totaal;Opgenomen;Resterend;Ziek (u);Bijzonder (u);Overuren verdiend;Overuren ingezet;Overuren saldo;Status';
  const rows = medewerkers
    .filter(m => m.Rol !== 'flexkracht')
    .sort((a, b) => a.Naam.localeCompare(b.Naam))
    .map(m => {
      const mwA = verlofaanvragen.filter(v => v.medewerkerId === m.Id && v.Status === 'goedgekeurd' && new Date(v.StartDatum).getFullYear() === jaar);
      const verlofOpg = mwA.filter(v => { const vt = verlofTypes.find(t => t.key === v.Type); return vt && vt.aftrekSaldo !== 'flex' && vt.aftrekSaldo !== 'geen'; })
        .reduce((s, v) => s + Math.max(0, (v.Uren || 0) - (v.overurenGebruikt || 0)), 0);
      const ziek = mwA.filter(v => v.Type === 'ziek').reduce((s, v) => s + (v.Uren || 0), 0);
      const bijz = mwA.filter(v => v.Type === 'bijzonder').reduce((s, v) => s + (v.Uren || 0), 0);
      const mwOu = overuren.filter(o => o.medewerkerId === m.Id && o.Status === 'goedgekeurd');
      const ouV = mwOu.reduce((s, o) => s + (o.Uren || o.uren || 0), 0);
      const ouG = mwA.reduce((s, v) => s + (v.overurenGebruikt || 0), 0);
      const tot = m.BerekendeVerlofUren + (m.MeegenomenUren || 0);
      return [m.Naam, m.Afdeling || '', m.ContractUren, m.BerekendeVerlofUren, m.MeegenomenUren || 0,
        tot, verlofOpg, tot - verlofOpg, ziek, bijz, ouV, ouG, ouV - ouG, m.Status || 'actief'].join(';');
    });
  downloadCSV(`slaaploods-medewerkers-${jaar}.csv`, header, rows);
}

export function leesCSVBestand(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Fout bij het lezen van het bestand.'));
    reader.readAsText(file);
  });
}
